import LogoSvg from 'assets/images/logo-blockchain-lightcolor.svg'
import { CeruleanButton, PlainButton, WhiteButton } from 'components/Button'
import Column from 'components/Column'
import I18n from 'components/I18n'
import iconColor from 'components/iconColor'
import FasBars from 'components/Legacy/Icons/FasBars'
import FasTimes from 'components/Legacy/Icons/FasTimes'
import CommonLink from 'components/Link'
import Row from 'components/Row'
import messages from 'lang'
import React, { useState } from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { useNavigate } from 'react-router'
import { getSupportLink } from 'services/urlService'
import styled, { css } from 'styled-components'
import { redirectToWalletSignin, redirectToWalletSignup } from 'utils'

import PageNavigation from '../PageNavigation'
import { PropsType as _P } from '.'

const WhiteSpace = css`
  white-space: nowrap;
`

const HeaderButton = styled(PlainButton)`
  ${WhiteSpace};
`

const SignupButton = styled(CeruleanButton)`
  ${WhiteSpace};
  margin-left: 1rem;
`

const SigninButton = styled(WhiteButton)`
  ${WhiteSpace};
`

const MobileBackground = styled.div<{ isActive: boolean }>`
  background: ${(props) => props.theme.palette.grey800};
  border-radius: 0 0 3px 3px;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
`

const LoggedoutButtons = styled(Row)`
  justify-content: flex-end;
  gap: 0.5rem;
`

const CompactRow = styled(Row)`
  padding: 0 1rem;

  &:first-child {
    padding: 0.75rem 1rem;
  }

  &:last-child {
    margin: 1rem;
  }
`

const Bars = styled(FasBars)`
  ${iconColor('white')};
`

const Times = styled(FasTimes)`
  align-self: center;
  ${iconColor('white')};
`

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
`

const Logo = styled.img.attrs<{}>(() => ({
  src: LogoSvg
}))`
  height: 2rem;
  width: 2rem;
`

const Link = styled(CommonLink)`
  padding: 1rem;
`

type Props = _P & WrappedComponentProps
const SignedOut = ({ intl, isCompact, walletUrl }: Props) => {
  const navigate = useNavigate()
  const [isActive, setIsActive] = useState(false)
  const onSignupClick = () => {
    setIsActive(false)
    redirectToWalletSignup(walletUrl)
  }
  const onSigninClick = () => {
    setIsActive(false)
    redirectToWalletSignin(walletUrl)
  }
  const handleClick = () => setIsActive(!isActive)

  if (!isCompact) {
    return (
      <>
        <PageNavigation />

        <LoggedoutButtons>
          <HeaderButton e2e='headerSignin' onClick={onSigninClick} size='md'>
            <I18n data={messages.button_signin} />
          </HeaderButton>
          <SignupButton e2e='headerSignup' onClick={onSignupClick} size='md'>
            <I18n data={messages.button_header_signup} />
          </SignupButton>
        </LoggedoutButtons>
      </>
    )
  }

  if (!isActive) {
    return (
      <Column>
        <Bars data-e2e='mobileMenu' onClick={handleClick} selectable size='24px' />
      </Column>
    )
  }

  return (
    <Column>
      <MobileBackground isActive={isActive}>
        <CompactRow justifyContent='space-between'>
          <LogoContainer
            onClick={() => {
              navigate('/trade')
              setIsActive(false)
            }}
          >
            <Logo />
          </LogoContainer>
          <Times onClick={handleClick} selectable size='24px' />
        </CompactRow>

        <CompactRow>
          <Link
            data-e2e='goToMarketsPage'
            onClick={() => {
              navigate('/market')

              setIsActive(false)
            }}
          >
            <I18n data={messages.markets} />
          </Link>
        </CompactRow>

        <CompactRow>
          <Link data-e2e='goToFeaturesPage' href='/features'>
            <I18n data={messages.button_features} />
          </Link>
        </CompactRow>

        <CompactRow>
          <Link data-e2e='goToFeesPage' href='/fees'>
            <I18n data={messages.button_fees} />
          </Link>
        </CompactRow>

        <CompactRow>
          <Link data-e2e='goToAffiliatePage' href='/affiliate'>
            <I18n data={messages.button_affiliate_program} />
          </Link>
        </CompactRow>

        <CompactRow>
          <Link href={getSupportLink(intl.locale)} rel='noreferrer' target='_blank'>
            <I18n data={messages.button_support} />
          </Link>
        </CompactRow>

        <CompactRow>
          <SigninButton e2e='headerSignin' onClick={onSigninClick} size='md'>
            <I18n data={messages.button_signin} />
          </SigninButton>
          <SignupButton e2e='headerSignup' onClick={onSignupClick} size='md'>
            <I18n data={messages.button_header_signup} />
          </SignupButton>
        </CompactRow>
      </MobileBackground>
    </Column>
  )
}

export default injectIntl(SignedOut)
